import { API_URL } from '../utils/ConfigUtils';

export interface EmailConfig {
  id?: string;
  uuid?: string;
  email: string;
  password?: string;
  smtp_host: string;
  smtp_port: number;
  smtp_tls: 'tls' | 'starttls' | 'none';
  tenant_id?: string | null;
  is_default: boolean;
  is_active: boolean;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
}

export interface EmailConfigResponse {
  email_config: EmailConfig;
  message?: string;
  test_result?: {
    success: boolean;
    message: string;
  };
}

export interface EmailConfigsListResponse {
  email_configs: EmailConfig[];
}

/**
 * Create a new email configuration
 */
export const createEmailConfig = async (emailConfig: EmailConfig, testEmail: boolean = false): Promise<EmailConfigResponse> => {
  const response = await fetch(`${API_URL}/email-configs`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('token')}`
    },
    body: JSON.stringify({
      ...emailConfig,
      test_email: testEmail
    })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to create email configuration');
  }

  return await response.json();
};

/**
 * Get all email configurations
 */
export const getEmailConfigs = async (tenantId?: string, isActive: boolean = true): Promise<EmailConfigsListResponse> => {
  let url = `${API_URL}/email-configs?is_active=${isActive}`;
  if (tenantId) {
    url += `&tenant_id=${tenantId}`;
  }

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch email configurations');
  }

  return await response.json();
};

/**
 * Get a specific email configuration
 */
export const getEmailConfig = async (uuid: string): Promise<EmailConfigResponse> => {
  const response = await fetch(`${API_URL}/email-configs/${uuid}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch email configuration');
  }

  return await response.json();
};

/**
 * Update an existing email configuration
 */
export const updateEmailConfig = async (uuid: string, emailConfig: Partial<EmailConfig>, testEmail: boolean = false): Promise<EmailConfigResponse> => {
  const response = await fetch(`${API_URL}/email-configs/${uuid}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('token')}`
    },
    body: JSON.stringify({
      ...emailConfig,
      test_email: testEmail
    })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to update email configuration');
  }

  return await response.json();
};

/**
 * Delete an email configuration
 */
export const deleteEmailConfig = async (uuid: string): Promise<{ message: string }> => {
  const response = await fetch(`${API_URL}/email-configs/${uuid}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to delete email configuration');
  }

  return await response.json();
};

/**
 * Set an email configuration as default
 */
export const setDefaultEmailConfig = async (uuid: string): Promise<EmailConfigResponse> => {
  const response = await fetch(`${API_URL}/email-configs/set-default/${uuid}`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to set default email configuration');
  }

  return await response.json();
};
